import { useCallback } from "react";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import type { ToolInterface } from "../../../api/content/authoring/processContentDecoders";
import type { StepCatalogue } from "../../../api/content/authoring/stepCatalogueApi";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";

/** A built-in tool's reference: `builtin:<name>@<version>` (P-3). */
export function isBuiltInTool(tool: string): boolean {
  return tool.startsWith("builtin:");
}

export async function readToolInterface(tool: string, catalogue: StepCatalogue | null, signal: AbortSignal): Promise<ToolInterface | null> {
  if (tool === "") {
    return null;
  }
  if (isBuiltInTool(tool)) {
    return catalogue?.builtInTools.find((builtIn) => builtIn.reference === tool)?.interface ?? null;
  }
  const item = await readAuthoredItem(tool, signal);
  return item.content?.kind === "tool" ? item.content.definition : null;
}

/** The declared interface of the tool a tool step names; `null` while none is chosen. */
export function useToolInterface(tool: string, catalogue: StepCatalogue | null): LoadState<ToolInterface | null> {
  const load = useCallback((signal: AbortSignal) => readToolInterface(tool, catalogue, signal), [tool, catalogue]);
  return useLoad(load).state;
}
